import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  accountId: string | null;
  email: string | null;
  error: string | null;
}

export function useAuth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    accountId: null,
    email: null,
    error: null,
  });

  // Handle OAuth callback - check for token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (token) {
      // Store token and clean URL
      api.setToken(token);
      setSearchParams({}, { replace: true });
      setState(prev => ({ ...prev, isLoading: true }));
    }

    if (error) {
      setState(prev => ({
        ...prev,
        error: `Authentication failed: ${error}`,
        isLoading: false,
      }));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Validate token on mount and after setting
  useEffect(() => {
    const validateAuth = async () => {
      const token = api.getToken();
      
      if (!token) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          accountId: null,
          email: null,
          error: null,
        });
        return;
      }

      try {
        const result = await api.validateToken();
        setState({
          isAuthenticated: result.valid,
          isLoading: false,
          accountId: result.accountId,
          email: result.email,
          error: null,
        });
      } catch (err) {
        console.error('Token validation failed:', err);
        api.setToken(null);
        setState({
          isAuthenticated: false,
          isLoading: false,
          accountId: null,
          email: null,
          error: null,
        });
      }
    };

    validateAuth();
  }, [searchParams]);

  const login = useCallback(() => {
    // Redirect to Gmail OAuth
    window.location.href = api.getGmailAuthUrl();
  }, []);

  const logout = useCallback(() => {
    api.setToken(null);
    setState({
      isAuthenticated: false,
      isLoading: false,
      accountId: null,
      email: null,
      error: null,
    });
    navigate('/login');
  }, [navigate]);

  return {
    ...state,
    login,
    logout,
  };
}
